import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  Flex,
  Image,
  Radio,
  SimpleGrid,
  Space,
  Text,
  Title
} from '@mantine/core';
import { IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';

import { PageLoading } from '@/pages/PageLoading';
import type { ArtistPhotoFormKey } from '@/pages/artist/onboard/components/stages/photo/hooks/useArtistPhoto.ts';
import { useArtistPhoto } from '@/pages/artist/onboard/components/stages/photo/hooks/useArtistPhoto.ts';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBModalLayout } from '@/shared/layouts';
import { LBPhotoDropZone } from '@/shared/modules';
import { LBButton } from '@/shared/ui-kit/components';

export const ArtistPhoto = () => {
  const {
    state: { isLoading, form, IsProfile, isValidForm, isSelectedProfilePhoto },
    functions
  } = useArtistPhoto();
  const [opened, { close, open }] = useDisclosure(false);
  const size = import.meta.env.VITE_PHOTO_MAX_SIZE as string;

  if (isLoading) return <PageLoading />;

  return (
    <>
      <LBModalLayout opened={opened} withCloseButton={false} onClose={close}>
        <Space h={4} />
        <Title order={2}>{translate('popups.selectMainPhoto.title')}</Title>
        <Space h={8} />
        <Text>{translate('popups.selectMainPhoto.description')}</Text>
        <Space h={32} />
        <Radio.Group
          value={IsProfile}
          onChange={functions.onSelectProfilePhoto}
        >
          <SimpleGrid cols={3}>
            {opened &&
              Object.entries(form.getValues()).map(([, value]) => (
                <Radio.Card key={value.tag} pos='relative' value={value.tag}>
                  <Radio.Indicator left={5} pos='absolute' top={5} />
                  <>
                    {typeof value.file === 'object' && (
                      <Image
                        fit='cover'
                        h={100}
                        src={URL.createObjectURL(value.file!)}
                        style={{ border: '1px solid black' }}
                        w='100%'
                        onLoad={() =>
                          URL.revokeObjectURL(URL.createObjectURL(value.file!))
                        }
                      />
                    )}

                    {typeof value.file === 'string' && (
                      <Image
                        fit='cover'
                        h={100}
                        src={resolveImagePath(value.file)}
                        style={{ border: '1px solid black' }}
                        w='100%'
                      />
                    )}
                  </>
                </Radio.Card>
              ))}
          </SimpleGrid>
        </Radio.Group>
        <Space h={32} />
        <LBButton fullWidth onClick={close}>
          {translate('commons.save')}
        </LBButton>
      </LBModalLayout>
      <form onSubmit={functions.onSubmit}>
        <Title order={1}>
          {translate('pages.onboarding.artist.photo.title')}
        </Title>
        <Space h={8} />
        <Text fz={14}>
          {translate('pages.onboarding.artist.photo.description')}
        </Text>
        <Space h={16} />

        {Object.entries(form.getValues()).map(([key, value], index) => (
          <div key={key}>
            <Text fz={14}>
              {index + 1}. {value.description}
            </Text>
            <Space h={4} />
            <LBPhotoDropZone
              accept={IMAGE_MIME_TYPE}
              disabled={form.submitting}
              maxFiles={1}
              initialValue={
                form.getValues()[key as ArtistPhotoFormKey].file !== null
                  ? [`${form.getValues()[key as ArtistPhotoFormKey].file}`]
                  : []
              }
              onDelete={() =>
                functions.onDeletePhoto(
                  value.photoId,
                  key as ArtistPhotoFormKey
                )
              }
              onFilesChange={(file) => {
                form.setFieldValue(`${key}.file`, file[0]);
              }}
            >
              <Flex align='center' direction='column' py={70}>
                <PhotoIcon color='gray' width={64} />
                <Text c='blue' fw={700} fz='sm'>
                  {translate('commons.uploadFromDevice')}
                </Text>
                <Text fz={12} ta='center'>
                  {translate('commons.photoParams', {
                    size
                  })}
                </Text>
              </Flex>
            </LBPhotoDropZone>
            <Space h={16} />
          </div>
        ))}
        <Space h={32} />
        <LBButton
          fullWidth
          disabled={!isValidForm}
          loading={form.submitting}
          variant='outline'
          onClick={open}
        >
          {translate('pages.onboarding.artist.photo.selectMainPhoto')}
        </LBButton>
        <Space h={8} />
        <LBButton
          fullWidth
          disabled={!isSelectedProfilePhoto}
          loading={form.submitting}
          type='submit'
        >
          {translate('commons.continue')}
        </LBButton>
      </form>
    </>
  );
};
